import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import useFetchGroupContact from '../../../../hooks/useApi/useFetchGroupContact';
import usePostDeleteGroupContact from '../../../../hooks/useApi/usePostDeleteGroupContact';
import { ContactListType } from '../../types';
import { useTranslation } from 'react-i18next';

const { Box, Table, Button, TextField } = vp;

type ContactsTableProps = {
  groupList: ContactListType;
  group: vp.ContactGroupsLists;
  groupID: string;
  type: string;
  hasNewContacts: boolean;
  isReview: boolean;
  setHasNewContactsList: (e: boolean) => void;
  handleReview: (e: boolean) => void;
  handleList: (list: ContactListType) => void;
};

type FC = FunctionComponent<ContactsTableProps>;

const ContactsTable: FC = (props: ContactsTableProps) => {
  const { groupList, group, type, groupID, hasNewContacts, setHasNewContactsList, handleReview, handleList } = props;
  const [groupContactResponse, fetchGroupContact] = useFetchGroupContact({});
  const [deleteGroupContactResponse, postDeleteGroupContact] = usePostDeleteGroupContact({});
  const [shouldDeleteGroupContact, setShouldDeleteGroupContact] = useState(false);
  const [contactID, setContactID] = useState('');
  const [contactList, setContactList] = useState<ContactListType>({});
  const { t } = useTranslation('translations', { keyPrefix: 'Groups.Details.Contacts.Table' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  // get contact list
  useEffect(() => {
    fetchGroupContact({
      GroupID: groupID,
    });
    setHasNewContactsList(false);
  }, [hasNewContacts]);
  useEffect(() => {
    setContactList(groupList);
  }, [groupList]);
  useEffect(() => {
    fetchGroupContact({
      GroupID: groupID,
    });
  }, [groupID]);
  // delete group contact
  useEffect(() => {
    if (shouldDeleteGroupContact) {
      postDeleteGroupContact({
        ContactID: contactID,
      });
    }
    setShouldDeleteGroupContact(false);
  }, [shouldDeleteGroupContact]);

  useEffect(() => {
    if (deleteGroupContactResponse) {
      setHasNewContactsList(true);
    }
  }, [deleteGroupContactResponse]);

  useEffect(() => {
    if (groupContactResponse.Success) {
      const myContacts = Object.values(groupContactResponse.Contacts).map((contact) => ({
        Account: contact.Account,
        ClientReferenceNumber: contact.ClientReferenceNumber,
        IsActive: contact.IsActive,
        Token: contact.Token,
        Amount:
          Object.values(contactList).find((originalContact) => contact.Token === originalContact.Token)?.Amount || '',
        Currency: group.ACTCurrencies_ID === '1' ? 'USD' : 'CAD',
        FirstName: contact.AccountHolderName?.split(' ')[0],
        LastName: contact.AccountHolderName?.split(' ')[1],
        AccountHolderName: contact.AccountHolderName,
        ContactID: contact.ContactID,
        Message: 'Successfully submitted',
      }));
      setContactList(myContacts);
      handleList(myContacts);
    }
  }, [groupContactResponse]);

  const buildAmountLists = (amount: string, token: string) => {
    const myContacts = Object.values(contactList).map((contact) => ({
      ...contact,
      Amount: contact.Token === token ? amount.replaceAll(',', '') : contact.Amount,
    }));
    setContactList(myContacts);
    handleList(myContacts);
  };

  const tableProps: vp.TableProps = {
    name: 'group-list',
    columns: {
      contact: { header: t('contactName'), align: 'left' },
      account: { header: t('account'), align: 'left' },
      amount: { header: t('amount'), align: 'left' },
      reference: { header: t('reference'), align: 'left' },
      service: { header: t('service'), align: 'left' },
      actions: { header: '', align: 'right' },
    },
    rows:
      Object.values(contactList).map((contact, index) => ({
        contact: contact.AccountHolderName,
        account: contact.Account,
        amount: (
          <TextField
            key={`group-contact-amount-${contact.ContactID}`}
            name={`group-contact-amount-${index}`}
            width="120px"
            preset="currency"
            onBlur={() => {}}
            onChange={(e) => {
              buildAmountLists(e.target.value, contact.Token);
            }}
            value={contact.Amount}
            variant="outlined"
            height="37px"
          />
        ),
        reference: contact.ClientReferenceNumber,
        service: type === 'Payment' ? t('Row.Service.credit') : t('Row.Service.debit'),
        actions: (
          <Button
            id={`remove-group-contact-${index}`}
            label={t('Row.removeButton')}
            color="lined"
            noShadow
            onClick={() => {
              setContactID(contact.ContactID);
              setShouldDeleteGroupContact(true);
            }}
          />
        ),
      })) || [],
  };

  return (
    <Box mt="md" flx="col" gap="md">
      {groupContactResponse.Success && (
        <>
          <Table {...tableProps} />
          <Button
            id={`review-contact-${group.ID}`}
            label={tGlobal('Buttons.review')}
            width="140px"
            noShadow
            onClick={() => {
              handleReview(true);
            }}
          />
        </>
      )}
    </Box>
  );
};

export default ContactsTable;
